// El hilo de una carga: lista + envío optimista.
//
// SIN caché, a propósito (mismo motivo que el cuadro de muelles): servir cinco
// minutos de retraso en una conversación es peor que no tenerla. De ahí
// `staleTime: 0` y refetch al abrir y al volver a la ventana.
import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { comentariosApi } from '@/services/api'
import type { ApiResponse, ComentarioCarga } from '@/types'

/** Un rechazo del backend ya desenvuelto. **Código, no texto**: la pantalla es
 *  bilingüe y lo traduce por `cargasSalida.comentarios.errores.<codigo>`. */
export interface ErrorComentario {
  codigo: string
  valores?: Record<string, string | number>
}

export function errorComentarioDe(err: unknown): ErrorComentario {
  const detalle = (err as AxiosError<{ detail?: ErrorComentario }>)?.response?.data?.detail
  if (detalle && typeof detalle === 'object' && detalle.codigo) return detalle
  return { codigo: 'error_inesperado' }
}

/** Marca de autoría del mensaje provisional (el propio usuario). */
export const OID_PROVISIONAL = '__yo__'
export const PREFIJO_PENDIENTE = 'pendiente-'

type Cache = ApiResponse<ComentarioCarga[]> | undefined

/** El backend manda el más reciente primero; el hilo se lee de arriba abajo. */
export function enOrdenCronologico(lista: ComentarioCarga[]): ComentarioCarga[] {
  return [...lista].sort((a, b) => (a.creado ?? '').localeCompare(b.creado ?? ''))
}

/** Cada cuánto se pregunta por mensajes nuevos mientras el drawer está abierto. */
export const INTERVALO_SONDEO_MS = 5000

export function useComentarios(carga: string, abierto: boolean) {
  const qc = useQueryClient()
  const key = ['cargas-salida', carga, 'comentarios']
  // Hay un envío en vuelo: el caché lleva el mensaje provisional y un sondeo
  // que entrara ahora lo pisaría con una lista sin él (el mensaje desaparecería
  // y reaparecería al confirmar el servidor).
  const enviando = useRef(false)

  const lista = useQuery({
    queryKey: key,
    queryFn: () => {
      if (enviando.current) {
        const cacheado = qc.getQueryData<Cache>(key)
        if (cacheado) return Promise.resolve(cacheado)
      }
      return comentariosApi.list(carga)
    },
    // SONDEO, no push, a propósito. Azure corre con hasta 3 réplicas del
    // backend: un aviso en memoria («ha entrado un mensaje») sólo llegaría a los
    // clientes conectados a la MISMA réplica. En local, con una sola instancia,
    // funcionaría siempre; en producción fallaría de forma intermitente, que es
    // lo peor. Hacerlo bien exige un bus compartido entre réplicas (Redis o
    // Azure Web PubSub): es un proyecto aparte. Hasta entonces, sondeo.
    //
    // Sólo mientras está abierto: `abierto` falso (o el drawer desmontado) lo
    // para; si no, se acumularía una petición cada 5 s por cada drawer abierto
    // alguna vez. Y no sondea con la pestaña en segundo plano (por defecto).
    refetchInterval: abierto && !!carga ? INTERVALO_SONDEO_MS : false,
    enabled: abierto && !!carga,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    retry: false,
  })

  /** Optimista: onMutate, cancelQueries, snapshot, setQueryData, rollback en
   *  onError y onSettled invalida (patrón de `useCapturas`). */
  const enviar = useMutation({
    mutationFn: (texto: string) => comentariosApi.create(carga, { texto }),
    onMutate: async (texto: string) => {
      enviando.current = true
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<Cache>(key)
      const ahora = new Date().toISOString()
      const provisional: ComentarioCarga = {
        id: `${PREFIJO_PENDIENTE}${Date.now()}`, carga, empresa: '', texto,
        creado: ahora, usuarioOid: OID_PROVISIONAL,
      }
      qc.setQueryData<Cache>(key, (old) => ({
        updatedAt: old?.updatedAt ?? ahora,
        data: [provisional, ...(old?.data ?? [])],
      }))
      return { prev }
    },
    onError: (_e, _t, ctx) => {
      // Se retira el mensaje provisional; lo tecleado lo conserva el drawer.
      qc.setQueryData<Cache>(key, ctx?.prev)
    },
    onSettled: () => {
      enviando.current = false
      return qc.invalidateQueries({ queryKey: key })
    },
  })

  return {
    comentarios: enOrdenCronologico(lista.data?.data ?? []),
    isLoading: lista.isLoading,
    isError: lista.isError,
    error: lista.error ? errorComentarioDe(lista.error) : null,
    enviar,
  }
}
